// Scheduling Coverage Thresholds Storage
// Manages configurable thresholds that drive the resource scheduling health badges
// across the Overview widget, Scheduling tab, Dashboard alerts and Dashboard table.
//
// Phase 2: API-backed with module-level cache for synchronous reads.
// Falls back to localStorage/defaults if the API is unavailable.

import { apiFetch } from './api-error-handler';
import { getApiBaseUrl } from './data-service';

export interface SchedulingThresholds {
  /** Minimum coverage % to be considered "Fully Resourced". Default: 95 */
  fullyResourcedMin: number;
  /** Minimum coverage % to be considered "Partially Resourced". Default: 50 */
  partiallyResourcedMin: number;
  /** Last updated ISO timestamp (set automatically on save) */
  lastUpdated?: string;
}

const STORAGE_KEY = 'scheduling-thresholds';

export const DEFAULT_SCHEDULING_THRESHOLDS: SchedulingThresholds = {
  fullyResourcedMin: 95,
  partiallyResourcedMin: 50,
};

// ── Module-level cache ──────────────────────────────────────────────────────
let _cache: SchedulingThresholds = { ...DEFAULT_SCHEDULING_THRESHOLDS };
let _hasFetched = false;

/**
 * Synchronous read — returns the cached scheduling thresholds.
 * Falls back to localStorage for legacy data on first access.
 */
export function getSchedulingThresholds(): SchedulingThresholds {
  if (!_hasFetched) {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        _cache = { ...DEFAULT_SCHEDULING_THRESHOLDS, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('Error loading scheduling thresholds from localStorage:', error);
    }
  }
  return _cache;
}

/**
 * Fetch scheduling thresholds from the server and update the cache.
 * Should be called during app bootstrap and after saving.
 */
export async function fetchSchedulingThresholds(): Promise<SchedulingThresholds> {
  try {
    const baseUrl = getApiBaseUrl();
    const data = await apiFetch<SchedulingThresholds>(
      `${baseUrl}/settings/project/scheduling-thresholds`
    );
    _cache = { ...DEFAULT_SCHEDULING_THRESHOLDS, ...data };
    _hasFetched = true;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_cache)); } catch { /* ignore */ }
  } catch (error) {
    console.error('Error fetching scheduling thresholds from API:', error);
    if (!_hasFetched) {
      _cache = getSchedulingThresholds();
    }
  }
  return _cache;
}

/**
 * Save scheduling thresholds to the server and update the cache.
 */
export async function saveSchedulingThresholds(thresholds: SchedulingThresholds): Promise<void> {
  const baseUrl = getApiBaseUrl();
  const data = await apiFetch<SchedulingThresholds>(
    `${baseUrl}/settings/project/scheduling-thresholds`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(thresholds),
    }
  );
  _cache = { ...DEFAULT_SCHEDULING_THRESHOLDS, ...data };
  _hasFetched = true;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_cache)); } catch { /* ignore */ }
}

/**
 * Derive a health status string from a coverage percentage using the configured thresholds.
 */
export function deriveHealthStatus(
  coveragePercent: number,
  thresholds: SchedulingThresholds
): 'FullyResourced' | 'PartiallyResourced' | 'Unresourced' {
  if (coveragePercent >= thresholds.fullyResourcedMin) return 'FullyResourced';
  if (coveragePercent >= thresholds.partiallyResourcedMin) return 'PartiallyResourced';
  return 'Unresourced';
}
